import React, { useState, useEffect } from 'react';
import { Button, Form, Container, Modal, Spinner, ProgressBar } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { firestore, storage } from '../utils/firebase';
import { getAuth } from 'firebase/auth';
import Select from 'react-select';
import { doc, getDoc, setDoc, collection, getDocs, Timestamp } from 'firebase/firestore';

const LogMaintenanceRecord = () => {
    const [equipmentNumber, setEquipmentNumber] = useState(null);
    const [equipmentList, setEquipmentList] = useState([]);
    const [date, setDate] = useState('');
    const [maintenanceType, setMaintenanceType] = useState(null);
    const [activity, setActivity] = useState('');
    const [maintenanceDuration, setMaintenanceDuration] = useState({ quantity: '', unit: '' });
    const [downtime, setDowntime] = useState({ quantity: '', unit: '' });
    const [vendor, setVendor] = useState('');
    const [file, setFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [employeeList, setEmployeeList] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const jobCardId = location.state?.jobCardId ?? null;

    const maintenanceOptions = [
        { value: "Preventive Maintenance", label: "Preventive Maintenance" },
        { value: "Predictive Maintenance", label: "Predictive Maintenance" },
        { value: "Corrective Maintenance", label: "Corrective Maintenance" },
        { value: "Adaptive Maintenance", label: "Adaptive Maintenance" },
        { value: "Routine Maintenance", label: "Routine Maintenance" },
        { value: "Emergency Maintenance", label: "Emergency Maintenance" },
        { value: "Reliability Centered Maintenance", label: "Reliability Centered Maintenance" },
        { value: "Condition-Based Maintenance", label: "Condition-Based Maintenance" },
        { value: "Risk-based Maintenance", label: "Risk-based Maintenance" },
    ];

    useEffect(() => {
        const fetchData = async () => {
            const auth = getAuth();
            const currentUser = auth.currentUser;
            if (!currentUser) return;
            const userDoc = await getDoc(doc(firestore, 'users', currentUser.uid));
            const company = userDoc.data().company;

            // Fetch employees
            const usersSnapshot = await getDocs(collection(firestore, 'users'));
            const users = usersSnapshot.docs
                .filter(doc => doc.data().company === company)
                .map(doc => ({ value: doc.id, label: `${doc.data().name} ${doc.data().surname}` }));
            setEmployeeList(users);

            // Fetch equipment
            const equipmentSnapshot = await getDocs(collection(firestore, 'companies', company, 'equipment'));
            const equipments = equipmentSnapshot.docs.map(doc => ({
                value: doc.data().equipmentNumber,
                label: doc.data().equipmentNumber
            }));
            setEquipmentList(equipments);

            // Pre-fill from job card if applicable
            if (jobCardId) {
                const jobCardDoc = await getDoc(doc(firestore, 'companies', company, 'JobCards', jobCardId));
                if (jobCardDoc.exists()) {
                    const job = jobCardDoc.data();
                    setEquipmentNumber({ value: job.equipmentNumber, label: job.equipmentNumber });
                    setSelectedEmployee(users.find(emp => emp.value === job.assignedEmployeeId) ?? null);
                    setActivity(job.description);
                    setDate(job.plannedDate.toDate().toISOString().split('T')[0]);
                }
            }
            const state = location.state;
            if (state) {
                if (state.equipmentNumber) setEquipmentNumber({ value: state.equipmentNumber, label: state.equipmentNumber });
                if (state.activity) setActivity(state.activity);
                if (state.date) setDate(state.date);
                if (state.employee) setSelectedEmployee(employeeList.find(emp => emp.label === state.employee) ?? null);
            }

            setLoading(false);
        };

        fetchData();
    }, [jobCardId]);
      const resetForm = () => {
    setEquipmentNumber(null);
    setDate('');
    setMaintenanceType('');
    setActivity('');
    setMaintenanceDuration({ quantity: '', unit: '' });
    setDowntime({ quantity: '', unit: '' });
    setVendor('');
    setFile('');
    setUploadProgress(0);
    setShowModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      const userDoc = await getDoc(doc(firestore, 'users', currentUser.uid));
      const company = userDoc.data().company;

      const recordId = `${equipmentNumber.value}-${activity}-${date}`;
      const fileRef = storage.ref().child(recordId);
      const uploadTask = fileRef.put(file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          setIsSubmitting(false);
        },
        async () => {
          const proofUrl = await fileRef.getDownloadURL();

          const maintenanceRecord = {
            activity,
              approved: false,
            date: Timestamp.fromDate(new Date(date)),
              equipmentNumber: equipmentNumber.value,
              JobcardOpen: false,
            fullyApproved: false,
            logmaintenance: true,
            maintenanceType,
            maintenanceDuration,
            downtime,
            scheduled: jobCardId ? true : false,
            ScheduledApproved: jobCardId ? true : false,
            Confirmed: true,
            rejected: false,
            comment: 'none',
            employee: selectedEmployee?.label ?? null,
            proofUrl,
            linkedJobCardNumber: jobCardId ?? null
          };

          await setDoc(doc(firestore, 'companies', company, 'maintenanceLogs', recordId), maintenanceRecord);

          if (jobCardId) {
            await setDoc(doc(firestore, 'companies', company, 'JobCards', jobCardId), {
              status: 'Completed'
            }, { merge: true });
          }

          resetForm();
          setIsSubmitting(false);
          setShowModal(true);
        }
      );
    } catch (error) {
      console.error('Error logging maintenance:', error);
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    navigate('/equipment-maintenance');
  };

  if (loading) {
    return (
      <div className="loading-spinner-container">
        <Spinner animation="border" role="status" variant="success" />
      </div>
    );
  }

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
      <h1 className="mb-4">Log Maintenance Record</h1>
      <Form onSubmit={handleSubmit}>
        <Form.Group className="my-3">
          <Form.Label>Equipment Number</Form.Label>
          <Select value={equipmentNumber} onChange={setEquipmentNumber} options={equipmentList} required />
        </Form.Group>

        <Form.Group className="my-3">
          <Form.Label>Date</Form.Label>
          <Form.Control type="date" value={date} onChange={e => setDate(e.target.value)} required />
        </Form.Group>

        <Form.Group className="my-3">
          <Form.Label>Maintenance Type</Form.Label>
          <Select value={maintenanceOptions.find(option => option.value === maintenanceType)}
            onChange={option => setMaintenanceType(option.value)}
            options={maintenanceOptions}
            required />
        </Form.Group>

        <Form.Group className="my-3">
          <Form.Label>Activity</Form.Label>
          <Form.Control type="text" value={activity} onChange={e => setActivity(e.target.value)} required />
        </Form.Group>

        <Form.Group className="my-3">
          <Form.Label>Duration of Maintenance</Form.Label>
          <div style={{ display: 'flex' }}>
            <Form.Control type="number" min="0" value={maintenanceDuration.quantity}
              onChange={e => setMaintenanceDuration({ ...maintenanceDuration, quantity: e.target.value })}
              required style={{ marginRight: '10px' }} />
            <Form.Select value={maintenanceDuration.unit}
              onChange={e => setMaintenanceDuration({ ...maintenanceDuration, unit: e.target.value })} required>
              <option value="">Select Unit</option>
              <option value="days">Days</option>
              <option value="hours">Hours</option>
            </Form.Select>
          </div>
        </Form.Group>

        <Form.Group className="my-3">
          <Form.Label>Downtime</Form.Label>
          <div style={{ display: 'flex' }}>
            <Form.Control type="number" min="0" value={downtime.quantity}
              onChange={e => setDowntime({ ...downtime, quantity: e.target.value })}
              required style={{ marginRight: '10px' }} />
            <Form.Select value={downtime.unit}
              onChange={e => setDowntime({ ...downtime, unit: e.target.value })} required>
              <option value="">Select Unit</option>
              <option value="days">Days</option>
              <option value="hours">Hours</option>
            </Form.Select>
          </div>
        </Form.Group>

        <Form.Group className="my-3">
          <Form.Label>Employee</Form.Label>
          <Select value={selectedEmployee} onChange={setSelectedEmployee} options={employeeList}
            isLoading={loading} required />
        </Form.Group>

        <Form.Group className="my-3">
          <Form.Label>Vendor/Contractor</Form.Label>
          <Form.Control type="text" value={vendor} onChange={e => setVendor(e.target.value)} required />
        </Form.Group>

        <Form.Group className="my-3">
          <Form.Label>Upload Supporting Document</Form.Label>
          <Form.Control type="file" onChange={e => setFile(e.target.files[0])} required />
          {uploadProgress > 0 && uploadProgress < 100 && (
            <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
          )}
        </Form.Group>

        <div className="my-3">
          <Button type="submit" variant="success" disabled={isSubmitting}>
            {isSubmitting ? <Spinner animation="border" size="sm" /> : 'Log Maintenance'}
          </Button>
          <Button variant="outline-success" onClick={() => navigate('/equipment-maintenance')} className="ms-2">
            Back
          </Button>
        </div>
      </Form>

      <Modal show={showModal} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>Maintenance record logged successfully!</Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={resetForm}>Log Another</Button>
          <Button variant="success" onClick={handleModalClose}>Back to Maintenance</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LogMaintenanceRecord;

